import type { RscCacheStatus, RscDelivery } from './rsc-cache-observer'

export type WebappLoadSurface =
  'pages' | 'assets' | 'facets' | 'main_categories' | 'categories' | 'subcategories' | 'contents'

export type WebappLoadOperation =
  | 'grid_navigation'
  | 'page_load'
  | 'browse'
  | 'search'
  | 'filter'
  | 'pagination'
  | 'initial_listing'
  | 'section_load'
  | 'filter_options'

export type WebappLoadTimings = {
  service?: 'meilisearch' | 'next_api'
  request_ms?: number
  service_processing_ms?: number
  api_server_ms?: number
  preparation_ms?: number
  server_data_ms?: number
  server_prepare_ms?: number
  ttfb_ms?: number
  response_download_ms?: number
  fcp_ms?: number
  navigation_data_ms?: number
  grid_presentation_ms?: number
  rsc_ms?: number
  thumbnails_ready?: boolean
}

export type WebappLoadPerformancePayload = WebappLoadTimings & {
  surface: WebappLoadSurface
  operation: WebappLoadOperation
  duration_ms: number
  status: 'success' | 'error'
  source?: 'client' | 'server_render' | 'document'
  main_category?: string
  category?: string
  sub_category?: string
  content?: string
  page?: number
  filters_active?: boolean
  result_count?: number
  status_code?: number
  error_type?: string
  error_code?: string
  navigation_id?: string
  origin_path?: string
  page_path?: string
  rsc_cache_status?: RscCacheStatus
  rsc_delivery?: RscDelivery
  rsc_request_count?: number
  destination_path?: string
}

type WebappLoadContext = Omit<
  WebappLoadPerformancePayload,
  'duration_ms' | 'status' | 'result_count' | 'status_code' | 'error_type'
>

const EVENT_NAME = 'webapp_load_performance'

export function sendWebappLoadPerformance(payload: WebappLoadPerformancePayload): void {
  if (typeof window === 'undefined') {
    return
  }

  const pagePath =
    new URL(
      payload.page_path ?? payload.destination_path ?? window.location.href,
      window.location.href
    ).pathname.replace(/\/+$/, '') || '/'
  const send = (session: ProductionCrateGlobalSession | null | undefined) => {
    const sendCustomEvent = session?.analytics?.sendCustomEvent

    if (typeof sendCustomEvent !== 'function') {
      return
    }

    try {
      sendCustomEvent(EVENT_NAME, { ...payload, page_path: pagePath, telemetry_version: 2 })
    } catch (error) {
      console.debug('webapp load performance event failed', { payload, error })
    }
  }

  const session = window.getGlobalSession?.()

  if (session?.analytics?.sendCustomEvent) {
    send(session)
    return
  }

  if (typeof window.waitForGlobalSession === 'function') {
    void window
      .waitForGlobalSession()
      .then(send)
      .catch(() => undefined)
  }
}

export async function measureWebappLoad<T>(
  context: WebappLoadContext,
  load: () => Promise<T>,
  getResultCount?: (result: T) => number,
  options: {
    signal?: AbortSignal
    getTimings?: () => WebappLoadTimings
    shouldReport?: () => boolean
  } = {}
): Promise<T> {
  const shouldMeasure = typeof window !== 'undefined'
  const startedAt = shouldMeasure ? performance.now() : 0

  try {
    const result = await load()

    if (shouldMeasure && (options.shouldReport?.() ?? true)) {
      sendWebappLoadPerformance({
        ...context,
        ...options.getTimings?.(),
        source: context.source ?? 'client',
        duration_ms: Math.round(performance.now() - startedAt),
        status: 'success',
        ...(getResultCount ? { result_count: getResultCount(result) } : {}),
      })
    }

    return result
  } catch (error) {
    if (shouldMeasure && !isCancelledLoad(error, options.signal)) {
      sendWebappLoadPerformance({
        ...context,
        ...options.getTimings?.(),
        source: context.source ?? 'client',
        duration_ms: Math.round(performance.now() - startedAt),
        status: 'error',
        ...describeLoadError(error),
      })
    }

    throw error
  }
}

export function isCancelledLoad(error: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) return true
  const seen = new Set<unknown>()
  let current = error
  while (current && typeof current === 'object' && !seen.has(current)) {
    seen.add(current)
    const cause = current as { name?: unknown; code?: unknown; cause?: unknown }
    if (cause.name === 'AbortError' || cause.name === 'CanceledError' || cause.code === 'ERR_CANCELED') return true
    current = cause.cause
  }
  return false
}

export function describeLoadError(
  error: unknown
): Pick<WebappLoadPerformancePayload, 'error_type' | 'error_code' | 'status_code'> {
  const value =
    error && typeof error === 'object'
      ? (error as { name?: string; code?: string; cause?: { code?: string }; response?: { status?: number } })
      : undefined
  return {
    error_type: value?.name ?? 'unknown',
    ...(value?.response?.status ? { status_code: value.response.status } : {}),
    ...(value?.cause?.code || value?.code ? { error_code: String(value.cause?.code ?? value.code).slice(0, 100) } : {}),
  }
}

export async function measureSearchRequest<T extends { processingTimeMs?: number }>(
  timings: WebappLoadTimings,
  request: () => Promise<T>
): Promise<T> {
  const startedAt = performance.now()
  try {
    const result = await request()
    if (Number.isFinite(result.processingTimeMs)) timings.service_processing_ms = result.processingTimeMs
    return result
  } finally {
    timings.request_ms = Math.round(performance.now() - startedAt)
  }
}
